import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/**
 * Shipping method record returned by the Carrier microservice.
 * `type === 'Standard'` flags an OrderMesh system default that hasn't been
 * overridden by the merchant. Any merchant-specific record is `'Override'`.
 * `parentMerchantId` is set on records inherited by a child merchant from
 * its parent — those are read-only until the child explicitly overrides
 * (via bulk-create-and-replace).
 */
export interface ShippingMethod {
  id: string;
  name: string;
  sla: string;
  shippingCountries: string[];
  isDefault: boolean;
  defaultShippingMethodId?: string;
  merchantId?: string;
  parentMerchantId?: string;
  type?: 'Standard' | 'Override';
}

/** Shape submitted to POST /v1/shipping-methods or per-row PUT. */
export interface ShippingMethodInput {
  name: string;
  sla: string;
  shippingCountries: string[];
  isDefault: boolean;
  merchantId: string;
  type: 'Standard' | 'Override';
}

/**
 * Carrier microservice slice. Covers the merchant Shipping (Fulfillment)
 * surface — shipping methods CRUD, bulk replace (for transitioning from
 * OrderMesh defaults to merchant-specific overrides, or for child merchants
 * inheriting from a parent), and default-method assignment.
 *
 * Endpoints (portalnew/portal/src/app/services/carrierServices.js):
 * - GET    /v1/shipping-methods?merchantId={id}[&countryCode=&type=]
 * - POST   /v1/shipping-methods
 * - PUT    /v1/shipping-methods/{id}
 * - DELETE /v1/shipping-methods/{id}
 * - POST   /v1/shipping-methods/bulk          (body: { bulk: [...] })
 * - DELETE /v1/shipping-methods/bulk          (body: { bulk: ids })
 * - POST   /v1/default-shipping-methods
 * - DELETE /v1/default-shipping-methods/{id}
 */
export const carrierApi = createApi({
  reducerPath: 'carrierApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['ShippingMethods'],
  endpoints: (builder) => ({
    getShippingMethods: builder.query<
      ShippingMethod[],
      { merchantId: string; countryCode?: string; type?: string }
    >({
      query: ({ merchantId, countryCode, type }) => {
        const params = new URLSearchParams();
        params.set('merchantId', merchantId);
        if (countryCode) params.set('countryCode', countryCode);
        if (type) params.set('type', type);
        return {
          url: `${services.carrier}/v1/shipping-methods?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (
        response: ShippingMethod[] | { shippingMethods?: ShippingMethod[] },
      ) =>
        Array.isArray(response) ? response : (response.shippingMethods ?? []),
      providesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    /**
     * OrderMesh system shipping options (`?type=Standard`, no merchant scope) —
     * the "System Shipping Options" picklist used by the vendor facility
     * shipping-mapping editor. Mirrors the legacy `getShippingMethods({ type:
     * 'Standard' })` call.
     */
    getStandardShippingMethods: builder.query<ShippingMethod[], void>({
      query: () => ({
        url: `${services.carrier}/v1/shipping-methods?type=Standard`,
        method: 'GET',
      }),
      transformResponse: (
        response: ShippingMethod[] | { shippingMethods?: ShippingMethod[] },
      ) => (Array.isArray(response) ? response : (response.shippingMethods ?? [])),
      providesTags: () => [{ type: 'ShippingMethods', id: 'STANDARD' }],
    }),

    createShippingMethod: builder.mutation<ShippingMethod, ShippingMethodInput>({
      query: (data) => ({
        url: `${services.carrier}/v1/shipping-methods`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    updateShippingMethod: builder.mutation<
      ShippingMethod,
      { id: string; data: ShippingMethodInput }
    >({
      query: ({ id, data }) => ({
        url: `${services.carrier}/v1/shipping-methods/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, { data }) => [
        { type: 'ShippingMethods', id: data.merchantId },
      ],
    }),

    deleteShippingMethod: builder.mutation<
      void,
      { id: string; merchantId: string }
    >({
      query: ({ id }) => ({
        url: `${services.carrier}/v1/shipping-methods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    bulkCreateAndReplaceShippingMethods: builder.mutation<
      ShippingMethod[],
      { merchantId: string; bulk: ShippingMethodInput[] }
    >({
      query: ({ bulk }) => ({
        url: `${services.carrier}/v1/shipping-methods/bulk`,
        method: 'POST',
        body: { bulk },
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    bulkDeleteShippingMethods: builder.mutation<
      void,
      { merchantId: string; ids: string[] }
    >({
      query: ({ ids }) => ({
        url: `${services.carrier}/v1/shipping-methods/bulk`,
        method: 'DELETE',
        body: { bulk: ids },
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    createDefaultShippingMethod: builder.mutation<
      { id: string },
      { merchantId: string; shippingMethodId: string }
    >({
      query: (body) => ({
        url: `${services.carrier}/v1/default-shipping-methods`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),

    deleteDefaultShippingMethod: builder.mutation<
      void,
      { id: string; merchantId: string }
    >({
      query: ({ id }) => ({
        url: `${services.carrier}/v1/default-shipping-methods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'ShippingMethods', id: merchantId },
      ],
    }),
  }),
});

export const {
  useGetShippingMethodsQuery,
  useGetStandardShippingMethodsQuery,
  useCreateShippingMethodMutation,
  useUpdateShippingMethodMutation,
  useDeleteShippingMethodMutation,
  useBulkCreateAndReplaceShippingMethodsMutation,
  useBulkDeleteShippingMethodsMutation,
  useCreateDefaultShippingMethodMutation,
  useDeleteDefaultShippingMethodMutation,
} = carrierApi;
